import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import {colorPaper, colorWarningBackground, styleSecondaryButton} from "../App/theme";
import {
    cancelPhoneVerification,
    PHONE_VERIFICATION_STATUS,
    sendPhoneVerificationCode,
    verifyPhoneCode
} from "../API/User";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import {getQueryParamValue, isMobile} from "../App/utils";
import 'react-phone-number-input/style.css'
import Loader from "../Loaders/LocalLoader";

const LazyPhoneInput = React.lazy(() => import('react-phone-number-input'));

export function PhoneVerification({userProfile, setUserProfile}) {
    const [open, setOpen] = React.useState(true);
    const [loading, setLoading] = React.useState(false);
    const [phone, setPhone] = React.useState('');
    const [country, setCountry] = React.useState('');
    const [code, setCode] = React.useState('');
    const [doNotShow, setDoNotShow] = React.useState(false);
    const [status, setStatus] = React.useState(userProfile?.phoneVerificationStatus);

    // {"error":"Phone number already in use","status":400}
    const inProgress = status === PHONE_VERIFICATION_STATUS.IN_PROGRESS;
    const notVerified = status === PHONE_VERIFICATION_STATUS.NOT_VERIFIED;

    let title = '';
    let text = '';
    let buttonName = '';
    let subtext = '';

    if (notVerified) {
        title = 'Activate Your Bonus Tokens 🪙'
        text = 'Secure your bonus tokens by verifying your phone number.'
        subtext = `Your number will be used only for verification purposes and NOT for marketing.`
        buttonName = 'Send Code 📲';
    } else if (inProgress) {
        title = 'Complete Your Verification'
        text = 'Enter the verification code sent to your phone to complete the process.'
        buttonName = isMobile  ? 'Confirm 🪙' : 'Confirm Bonus 🪙';
    }

    const handleSubmit = async () => {
        if (notVerified && phone?.length < 6) return;
        if (inProgress && code?.length < 6) return;
        setLoading(true);
        if (notVerified) {
            const response = await sendPhoneVerificationCode(phone, country);
            setLoading(false);
            if (response?.status === 200) {
                setStatus(PHONE_VERIFICATION_STATUS.IN_PROGRESS)
            }
        } else if (inProgress) {
            let response;
            try {
                response = await verifyPhoneCode(code);
            } catch (e) {
                if (e?.response?.data?.status === 501) {
                    return setOpen(false)
                }
            } finally {
                setLoading(false);
            }

            if (response?.status === 200) {
                setUserProfile(prev => {
                    return {...prev, phoneVerificationStatus: PHONE_VERIFICATION_STATUS.VERIFIED}
                })
                setOpen(false)
            }
        }

    };

    const handleClose = async () => {
        if (doNotShow) {
            const res = await cancelPhoneVerification();
        }
        sessionStorage.setItem("phoneVerificationClosed", (+new Date()).toString());
        setOpen(false);
    };

    const phoneVerificationClosed = sessionStorage.getItem("phoneVerificationClosed");

    if (!getQueryParamValue('showPhoneVerification') && phoneVerificationClosed && (+new Date() - parseInt(phoneVerificationClosed)) < 1000 * 60 * 60 * 24 ) {
        return null;
    }

    return (
        <React.Fragment>
            <Loader open={loading} blocking={true} />
            <Dialog
                open={open}
                PaperProps={{
                    component: 'form',
                    onSubmit: (event) => {
                        event.preventDefault();
                    },
                }}
            >
                <DialogTitle sx={{backgroundColor: colorPaper}}>{title}</DialogTitle>
                <DialogContent sx={{backgroundColor: colorPaper}}>
                    <DialogContentText>
                        {text}
                        {subtext && <>
                            <br />
                            <span style={{fontSize: '0.7em'}}>{subtext}</span>
                        </>}
                    </DialogContentText>

                    {notVerified && <React.Suspense fallback={<div></div>}>
                        <LazyPhoneInput
                            international
                            defaultCountry="US"
                            style={{marginTop: '20px', marginBottom: '20px'}}
                            fullWidth={true}
                            onChange={(v) => {
                                if (v) {
                                    setPhone(v.replace(/[^\d+]/g, ''))
                                }
                            }}
                            onCountryChange={(countryCode) => {
                                if (countryCode) {
                                    setCountry(countryCode)
                                }
                            }}
                        />
                        <br />
                    </React.Suspense>}

                    {inProgress && <TextField
                        autoFocus
                        required
                        margin="dense"
                        id="code"
                        name="code"
                        label="Code"
                        value={code || ''}
                        onChange={(e) => setCode(e.target.value)}
                        fullWidth
                        variant="standard"
                    />}

                    <FormControlLabel
                        control={
                            <Checkbox
                                disabled={!!phone}
                                checked={doNotShow && !phone}
                                onChange={(e) => setDoNotShow(e.target.checked)}
                                name="doNotShow"
                            />
                        }
                        label="Do not show this message again and refuse bonus"
                    />
                </DialogContent>
                <DialogActions sx={{backgroundColor: colorPaper}}>
                    <Button onClick={handleClose}
                            variant="outlined"
                            fullWidth={isMobile}
                            sx={doNotShow ? {} : styleSecondaryButton}
                    >Cancel</Button>
                    <Button
                        disabled={!code && !phone}
                        variant="outlined"
                        fullWidth={isMobile}
                        color="secondary"
                        onClick={handleSubmit} type="submit">{buttonName}</Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}